import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Wrench, FileText } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_COLORS = { Approved: "#10B981", "In Review": "#3B82F6", Draft: "#94A3B8", Overdue: "#EF4444" };

export default function Engineering() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/engineering").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="kicker text-slate-500">Loading engineering workspace…</div>;

    const { deliverables, rfis, discipline_summary } = data;

    return (
        <div className="space-y-8">
            <div>
                <div className="kicker text-[var(--sgsb-gold)]">Engineering</div>
                <h1 className="mt-2 font-display font-black text-white text-4xl tracking-tighter">Engineering</h1>
                <p className="mt-2 text-slate-400 text-sm font-body max-w-2xl">
                    Discipline performance, deliverables and technical queries across all active packages.
                </p>
            </div>

            <div className="glass rounded-xl p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="kicker text-slate-400">Disciplines</div>
                        <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">Deliverable status by discipline</h3>
                    </div>
                    <Wrench className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                </div>
                <div className="h-[320px] mt-4 -ml-3">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={discipline_summary}>
                            <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" vertical={false} />
                            <XAxis dataKey="discipline" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                            <YAxis stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                            <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                            <Legend wrapperStyle={{ color: "#94A3B8", fontFamily: "Manrope", fontSize: 12 }} />
                            <Bar stackId="a" dataKey="approved" fill="#10B981" radius={[0, 0, 0, 0]} />
                            <Bar stackId="a" dataKey="in_review" fill="#3B82F6" radius={[0, 0, 0, 0]} />
                            <Bar stackId="a" dataKey="overdue" fill="#EF4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="glass rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-slate-800/60 flex items-center justify-between">
                        <h3 className="font-display font-black text-white tracking-tight flex items-center gap-2">
                            <FileText className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} /> Deliverables
                        </h3>
                        <span className="kicker text-slate-500 text-[10px]">{deliverables.length} records</span>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        <table className="w-full text-sm font-body">
                            <thead className="sticky top-0 bg-slate-900/95 backdrop-blur">
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Ref</th>
                                    <th className="text-left px-4 py-3">Discipline</th>
                                    <th className="text-left px-4 py-3">Status</th>
                                    <th className="text-left px-4 py-3">Due</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deliverables.map((d) => (
                                    <tr key={d.id} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                        <td className="px-4 py-2.5">
                                            <div className="text-slate-100">{d.ref}</div>
                                            <div className="text-[11px] text-slate-500">{d.title}</div>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-300">{d.discipline}</td>
                                        <td className="px-4 py-2.5">
                                            <span
                                                className="text-[11px] rounded-full px-2 py-0.5"
                                                style={{ background: `${STATUS_COLORS[d.status]}22`, color: STATUS_COLORS[d.status], border: `1px solid ${STATUS_COLORS[d.status]}55` }}
                                            >
                                                {d.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-400">{d.due_date}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="glass rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-slate-800/60 flex items-center justify-between">
                        <h3 className="font-display font-black text-white tracking-tight">Requests for Information</h3>
                        <span className="kicker text-slate-500 text-[10px]">{rfis.length} RFIs</span>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        <table className="w-full text-sm font-body">
                            <thead className="sticky top-0 bg-slate-900/95 backdrop-blur">
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Ref</th>
                                    <th className="text-left px-4 py-3">Subject</th>
                                    <th className="text-left px-4 py-3">Discipline</th>
                                    <th className="text-left px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rfis.map((r) => (
                                    <tr key={r.id} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                        <td className="px-4 py-2.5 text-slate-100">{r.ref}</td>
                                        <td className="px-4 py-2.5 text-slate-200">{r.subject}</td>
                                        <td className="px-4 py-2.5 text-slate-300">{r.discipline}</td>
                                        <td className="px-4 py-2.5 text-slate-400">{r.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
